import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';

/**
 * Detail page header with inline-editable title, status badge, subtitle, tags,
 * primary action buttons, and a secondary overflow menu.
 */
const CadenceDetailHeader = ({
  name,
  status,
  subtitle,
  tags,
  blueprintName,
  actions,
  overflowActions,
  isEditingName,
  editName,
  setEditName,
  onSubmitRename,
  onCancelRename,
  onStartRename,
}) => {
  const [showOverflow, setShowOverflow] = useState(false);
  const overflowRef = useRef(null);

  useEffect(() => {
    if (!showOverflow) return;
    const handleClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setShowOverflow(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showOverflow]);

  // Actions are pre-filtered by availableActions in the page — just filter falsy entries
  const visibleOverflowActions = (overflowActions || []).filter(Boolean);

  return (
    <div className="cadence-detail-header">
      <div className="cadence-detail-header--title-row">
        <div className="cadence-detail-header--title-info">
          {isEditingName ? (
            <input
              type="text"
              className="cadence-inline-title-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={onSubmitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSubmitRename();
                if (e.key === 'Escape') onCancelRename();
              }}
              autoFocus
            />
          ) : (
            <button type="button" className="cadence-inline-title-btn" onClick={onStartRename}>
              <h1 className="cadence-detail-header--title">{name}</h1>
              <Icon icon="edit" className="cadence-inline-title-edit-icon" />
            </button>
          )}
          {status && (
            <span className={`cadence-detail-header--status cadence-detail-header--status-${status.toLowerCase()}`}>
              {status}
            </span>
          )}
          {subtitle && <p className="cadence-detail-header--subtitle">{subtitle}</p>}
          {blueprintName && (
            <div className="cadence-detail-header--blueprint">
              <Icon icon="content-copy" iconKit="gt2" />
              <span>Created from blueprint: {blueprintName}</span>
            </div>
          )}
          {tags?.length > 0 && (
            <div className="cadence-detail-header--tags">
              {tags.map((tag) => (
                <span key={tag} className="cadence-detail-header--tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="cadence-detail-header--actions">
          {actions}
          {visibleOverflowActions.length > 0 && (
            <div className="cadence-detail-header--overflow" ref={overflowRef}>
              <button
                type="button"
                className="cadence-detail-header--more-btn"
                onClick={() => setShowOverflow((v) => !v)}
                aria-label="More actions"
              >
                <Icon icon="more-vert" />
              </button>
              {showOverflow && (
                <div className="cadence-detail-header--overflow-menu" role="menu">
                  {visibleOverflowActions.map((action) => (
                    <button
                      key={action.label}
                      type="button"
                      role="menuitem"
                      className={action.isDanger ? 'cadence-detail-header--overflow-danger' : ''}
                      onClick={() => {
                        action.onClick();
                        setShowOverflow(false);
                      }}
                    >
                      <Icon icon={action.icon} iconKit={action.iconKit || 'gt'} /> {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CadenceDetailHeader.propTypes = {
  name: PropTypes.string.isRequired,
  status: PropTypes.string,
  subtitle: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  blueprintName: PropTypes.string,
  actions: PropTypes.node,
  overflowActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      isDanger: PropTypes.bool,
    })
  ),
  isEditingName: PropTypes.bool,
  editName: PropTypes.string,
  setEditName: PropTypes.func,
  onSubmitRename: PropTypes.func,
  onCancelRename: PropTypes.func,
  onStartRename: PropTypes.func,
};

export default CadenceDetailHeader;
